import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const FAQSection = () => {
  const { t } = useLanguage();
  const [openIdx, setOpenIdx] = useState(0);

  const faqItems = Array.from({ length: 10 }, (_, i) => ({
    q: t[`faqs_q${i + 1}`] || "",
    a: t[`faqs_a${i + 1}`] || "",
  })).filter((it) => it.q && it.a);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 overflow-hidden themed-section"
      style={{ background: "var(--color-bg)" }}
    >
      {/* JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((it) => ({
              "@type": "Question",
              name: it.q,
              acceptedAnswer: { "@type": "Answer", text: it.a },
            })),
          }),
        }}
      />

      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <p
          className="font-['JetBrains_Mono'] text-xs tracking-[0.3em] uppercase mb-4 text-center"
          style={{ color: "var(--color-accent)" }}
        >
          {t.faqs_tag || "FAQ"}
        </p>
        <h2
          className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-4"
          style={{ color: "var(--color-text)" }}
        >
          {t.faqs_title || "Frequently asked questions"}
        </h2>
        <p
          className="text-center mb-12"
          style={{ color: "var(--color-text-muted)" }}
        >
          {t.faqs_subtitle || "Everything you need to know before your first scan"}
        </p>

        <div className="space-y-3">
          {faqItems.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <div
                key={idx}
                data-testid={`faq-item-${idx}`}
                className="rounded-lg border overflow-hidden transition-all"
                style={{
                  background: "var(--color-surface)",
                  borderColor: open ? "var(--color-primary)" : "var(--color-border)",
                }}
              >
                <button
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  data-testid={`faq-toggle-${idx}`}
                  aria-expanded={open}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:opacity-90 transition-opacity"
                >
                  <span
                    className="font-bold text-base sm:text-lg"
                    style={{ color: "var(--color-text)" }}
                  >
                    {item.q}
                  </span>
                  {open ? (
                    <Minus className="w-5 h-5 shrink-0" style={{ color: "var(--color-primary)" }} />
                  ) : (
                    <Plus className="w-5 h-5 shrink-0" style={{ color: "var(--color-text-muted)" }} />
                  )}
                </button>
                {open && (
                  <div
                    className="px-6 pb-5 text-sm sm:text-base leading-relaxed"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p
          className="text-center mt-10 text-sm"
          style={{ color: "var(--color-text-muted)" }}
        >
          {t.faqs_contact || "Need more? Email us at"}{" "}
          <a
            href="mailto:support@toprankpulse.com"
            className="underline font-bold"
            style={{ color: "var(--color-primary)" }}
          >
            support@toprankpulse.com
          </a>
        </p>
      </div>
    </section>
  );
};

export default FAQSection;
